import { useState, useEffect, useCallback, useRef } from "react";
import { useToolOpener } from "@/lib/dockBus";
import { useLocation } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Sparkles, HandHelping, Volume2, VolumeX, PlayCircle, Languages, ThumbsUp, ThumbsDown } from "lucide-react";
import { getGuide } from "@/data/guideContent";
import { useSettings, LANGUAGES } from "@/context/SettingsContext";
import api from "@/lib/api";

const SEEN_KEY = "pula_guide_seen_v1";
const WELCOMED_KEY = "pula_guide_welcomed_v1";
// Best-effort BCP-47 for on-device speech; unlisted languages fall back to default voice.
const SPEECH_LANG = { en: "en-GB", fr: "fr-FR", pt: "pt-PT", es: "es-ES", de: "de-DE", it: "it-IT", sw: "sw", af: "af-ZA", zu: "zu-ZA", xh: "xh-ZA", st: "st-ZA", tn: "tn-ZA" };

function readSeen() {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}"); } catch { return {}; }
}

export function PageGuide() {
  const location = useLocation();
  const guide = getGuide(location.pathname);
  const isPortal = location.pathname.startsWith("/portal");
  const { language, setLanguage } = useSettings();
  const [open, setOpen] = useState(false);
  useToolOpener("guide", () => setOpen(true));
  const [tourIndex, setTourIndex] = useState(null); // null = not touring
  const [rect, setRect] = useState(null);
  const [seenPulse, setSeenPulse] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [tr, setTr] = useState(null); // translated { title, intro, steps:[...] }
  const [translating, setTranslating] = useState(false);
  const [fbGiven, setFbGiven] = useState(false);
  const [langOffer, setLangOffer] = useState(null); // {code,label} or null
  const startRef = useRef(false);

  // Display strings: translated when available, else English.
  const disp = {
    title: tr?.title || guide.title,
    intro: tr?.intro || guide.intro,
    steps: guide.steps.map((s, i) => ({ ...s, text: (tr?.steps?.[i]) || s.text })),
  };

  // ---- Multilingual bubbles: translate once per (page, language), cached ----
  useEffect(() => {
    if (language === "en") { setTr(null); return; }
    const cacheKey = `pula_gtr_${guide.key}_${language}`;
    try { const c = localStorage.getItem(cacheKey); if (c) { setTr(JSON.parse(c)); return; } } catch {}
    setTr(null);
    setTranslating(true);
    const items = [guide.title, guide.intro, ...guide.steps.map((s) => s.text)];
    let cancelled = false;
    api.post("/guide/translate", { key: guide.key, lang: language, items })
      .then(({ data }) => {
        if (cancelled) return;
        const it = data?.items || [];
        if (it.length === items.length) {
          const obj = { title: it[0], intro: it[1], steps: it.slice(2) };
          setTr(obj);
          try { localStorage.setItem(cacheKey, JSON.stringify(obj)); } catch {}
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setTranslating(false); });
    return () => { cancelled = true; };
  }, [guide.key, language]);

  const trackOpen = useCallback(() => {
    api.post("/guide/open", { page: guide.key }).catch(() => {});
  }, [guide.key]);


  // ---- Read aloud (on-device Web Speech — low-data, no cost) ----
  const speechOk = typeof window !== "undefined" && "speechSynthesis" in window;
  const stopSpeak = useCallback(() => {
    if (speechOk) window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [speechOk]);
  const speak = useCallback((text) => {
    if (!speechOk || !text) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.98; u.pitch = 1.02;
    if (SPEECH_LANG[language]) u.lang = SPEECH_LANG[language];
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(u);
  }, [speechOk, language]);
  const toggleSpeakAll = useCallback(() => {
    if (speaking) { stopSpeak(); return; }
    const all = `${disp.title}. ${disp.intro} ` + disp.steps.map((s, i) => `${i + 1}. ${s.text}`).join(" ");
    speak(all);
  }, [speaking, stopSpeak, speak, disp]);

  // Auto-welcome the very first time a visitor lands anywhere on the site.
  useEffect(() => {
    if (startRef.current) return;
    startRef.current = true;
    const welcomed = localStorage.getItem(WELCOMED_KEY);
    if (!welcomed) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  // Auto-language: offer to show the guide in the visitor's phone language (once).
  useEffect(() => {
    if (localStorage.getItem("pula_lang_offered")) return;
    if (language !== "en") { localStorage.setItem("pula_lang_offered", "1"); return; }
    const nav = ((typeof navigator !== "undefined" && navigator.language) || "").slice(0, 2).toLowerCase();
    const match = LANGUAGES.find((l) => l.code === nav && l.code !== "en");
    if (match) setLangOffer(match);
    else localStorage.setItem("pula_lang_offered", "1");
  }, [language]);

  // Pulse the launcher when the current page has an unseen guide.
  useEffect(() => {
    const seen = readSeen();
    setSeenPulse(!seen[guide.key]);
    // close any open panel/tour when navigating
    setOpen(false);
    setTourIndex(null);
    stopSpeak();
    setFbGiven(false);
  }, [guide.key, stopSpeak]);

  // Demo Mode asks us to open the guide panel for the current page.
  useEffect(() => {
    const onOpen = () => { setOpen(true); trackOpen(); };
    window.addEventListener("pula-open-guide", onOpen);
    return () => window.removeEventListener("pula-open-guide", onOpen);
  }, [trackOpen]);

  const markSeen = useCallback(() => {
    const seen = readSeen();
    seen[guide.key] = true;
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(seen)); } catch {}
    localStorage.setItem(WELCOMED_KEY, "1");
    setSeenPulse(false);
  }, [guide.key]);

  const openPanel = () => { setOpen(true); markSeen(); trackOpen(); };
  const sendFeedback = (helpful) => { api.post("/guide/feedback", { page: guide.key, helpful }).catch(() => {}); setFbGiven(true); };
  const acceptLang = () => { if (langOffer) setLanguage(langOffer.code); localStorage.setItem("pula_lang_offered", "1"); setLangOffer(null); };
  const dismissLang = () => { localStorage.setItem("pula_lang_offered", "1"); setLangOffer(null); };
  const closeAll = () => { setOpen(false); setTourIndex(null); markSeen(); stopSpeak(); };

  // ---- Guided tour: spotlight the target element for the current step ----
  const computeRect = useCallback((idx) => {
    const step = guide.steps[idx];
    if (!step?.target) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) { setRect(null); return; }
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, 350);
  }, [guide.steps]);

  const startTour = () => { setOpen(false); setTourIndex(0); };

  useEffect(() => {
    if (tourIndex === null) { document.body.style.overflow = ""; return; }
    document.body.style.overflow = "hidden";
    computeRect(tourIndex);
    const onResize = () => computeRect(tourIndex);
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); document.body.style.overflow = ""; };
  }, [tourIndex, computeRect]);

  const touring = tourIndex !== null;
  const step = touring ? disp.steps[tourIndex] : null;

  // bubble placement for tour
  let bubbleStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  if (rect) {
    const below = rect.top + rect.height + 16;
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    if (spaceBelow > 200) {
      bubbleStyle = { top: below, left: Math.min(Math.max(rect.left, 12), window.innerWidth - 320), transform: "none" };
    } else {
      bubbleStyle = { top: Math.max(rect.top - 190, 12), left: Math.min(Math.max(rect.left, 12), window.innerWidth - 320), transform: "none" };
    }
  }

  return (
    <>
      {/* Floating mascot launcher */}
      <button
        data-testid="page-guide-launcher"
        onClick={openPanel}
        aria-label="Open page guide"
        className="fixed right-4 md:right-6 bottom-44 z-40 flex items-center gap-2 pl-1.5 pr-3.5 py-1.5 rounded-full bg-white border border-[var(--pula-border)] shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all">
        <span className="relative">
          <img src="/pula-guide.png" alt="Pula guide" className="w-9 h-9 rounded-full object-cover bg-pula-green/10" />
          {seenPulse && (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-pula-accent ring-2 ring-white animate-ping" />
          )}
          {seenPulse && (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-pula-accent ring-2 ring-white" />
          )}
        </span>
        <span className="text-sm font-heading font-bold text-pula-ink">Guide</span>
      </button>

      {/* Auto-language offer (first visit, once) */}
      {langOffer && (
        <div data-testid="page-guide-lang-offer" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[56] w-[92vw] max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl border border-[var(--pula-border)] p-3.5 flex items-center gap-3">
            <img src="/pula-guide.png" alt="" className="w-10 h-10 rounded-full object-cover bg-pula-green/10 shrink-0" />
            <p className="text-sm text-pula-ink font-body flex-1">Show Pula's guide in <span className="font-bold">{langOffer.label}</span>?</p>
            <button data-testid="page-guide-lang-accept" onClick={acceptLang}
              className="px-3 py-1.5 rounded-full bg-pula-green text-white text-xs font-heading font-bold hover:bg-pula-greenhover whitespace-nowrap">Yes please</button>
            <button data-testid="page-guide-lang-dismiss" onClick={dismissLang}
              className="text-pula-inksoft hover:text-pula-ink shrink-0" aria-label="No thanks"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Info panel with mascot + step bubbles */}
      {open && (
        <div className="fixed inset-0 z-[55] flex items-end md:items-center justify-center p-0 md:p-4" data-testid="page-guide-panel">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeAll} />
          <div className="relative w-full md:max-w-md bg-pula-bg rounded-t-3xl md:rounded-3xl shadow-2xl border border-[var(--pula-border)] overflow-hidden animate-in slide-in-from-bottom duration-300">
            <div className="bg-pula-green/10 px-5 pt-5 pb-4 flex items-start gap-3">
              <img src="/pula-guide.png" alt="Pula guide" className="w-14 h-14 rounded-2xl object-cover bg-white shadow-sm shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-heading font-extrabold text-pula-ink text-lg leading-tight">{disp.title}</p>
                <p className="text-sm text-pula-inksoft font-body mt-0.5">{disp.intro}</p>
              </div>
              <button data-testid="page-guide-close" onClick={closeAll} className="text-pula-inksoft hover:text-pula-ink shrink-0"><X className="w-5 h-5" /></button>
            </div>
            {(speechOk || language !== "en") && (
              <div className="px-5 pt-3 flex items-center gap-2 flex-wrap">
                {speechOk && (
                  <button data-testid="page-guide-listen" onClick={toggleSpeakAll}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pula-green/10 text-pula-green text-xs font-heading font-bold hover:bg-pula-green/20 transition-colors">
                    {speaking ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                    {speaking ? "Stop reading" : "Read aloud"}
                  </button>
                )}
                {language !== "en" && (
                  <span data-testid="page-guide-lang" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pula-accent/10 text-pula-accent text-xs font-heading font-bold">
                    <Languages className="w-3.5 h-3.5" /> {translating ? "Translating…" : "Translated"}
                  </span>
                )}
              </div>
            )}
            <div className="px-5 py-4 space-y-3 max-h-[50vh] overflow-y-auto">
              {disp.steps.map((s, i) => (
                <div key={i} className="flex gap-3" data-testid={`page-guide-step-${i}`}>
                  <span className="w-7 h-7 rounded-full bg-pula-green text-white grid place-items-center shrink-0 text-sm font-heading font-bold">{i + 1}</span>
                  <p className="text-sm text-pula-ink font-body leading-relaxed pt-0.5">{s.text}</p>
                </div>
              ))}
            </div>
            <div className="px-5 pb-1" data-testid="page-guide-feedback">
              {fbGiven ? (
                <p className="text-xs text-pula-green font-body font-semibold">Thanks — that helps us make Pula clearer. 💚</p>
              ) : (
                <div className="flex items-center gap-2 text-xs text-pula-inksoft font-body">
                  <span>Was this helpful?</span>
                  <button data-testid="page-guide-feedback-yes" onClick={() => sendFeedback(true)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[var(--pula-border)] hover:bg-pula-green/10 hover:text-pula-green transition-colors">
                    <ThumbsUp className="w-3.5 h-3.5" /> Yes
                  </button>
                  <button data-testid="page-guide-feedback-no" onClick={() => sendFeedback(false)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[var(--pula-border)] hover:bg-pula-accent/10 hover:text-pula-accent transition-colors">
                    <ThumbsDown className="w-3.5 h-3.5" /> Not really
                  </button>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-[var(--pula-border)] flex items-center gap-2">
              <button
                data-testid="page-guide-start-tour"
                onClick={startTour}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-pula-accent text-white text-sm font-heading font-bold hover:bg-pula-accent/90 transition-colors">
                <Sparkles className="w-4 h-4" /> Show me around
              </button>
              <button
                data-testid="page-guide-got-it"
                onClick={closeAll}
                className="px-4 py-2.5 rounded-full border border-[var(--pula-border)] text-sm font-heading font-bold text-pula-ink hover:bg-pula-bg2 transition-colors">
                Got it
              </button>
            </div>
            <p className="px-5 pb-4 text-[11px] text-pula-inksoft font-body text-center">
              We never sell your data · No hidden fees · In danger, always call 999.
            </p>
            {!isPortal ? (
              <button data-testid="page-guide-start-demo" onClick={() => { closeAll(); window.dispatchEvent(new CustomEvent("pula-start-demo")); }}
                className="w-full px-5 py-3 bg-pula-ink text-pula-bg text-xs font-heading font-bold inline-flex items-center justify-center gap-1.5 hover:bg-pula-ink/90 transition-colors">
                <PlayCircle className="w-4 h-4" /> Start full platform tour (great for demos)
              </button>
            ) : (
              <button data-testid="page-guide-start-demo-portal" onClick={() => { closeAll(); window.dispatchEvent(new CustomEvent("pula-start-demo-portal")); }}
                className="w-full px-5 py-3 bg-pula-ink text-pula-bg text-xs font-heading font-bold inline-flex items-center justify-center gap-1.5 hover:bg-pula-ink/90 transition-colors">
                <PlayCircle className="w-4 h-4" /> Start staff training tour (Clients · Cases · Safety)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Guided tour spotlight overlay */}
      {touring && (
        <div className="fixed inset-0 z-[60]" data-testid="page-guide-tour">
          {/* dim + spotlight hole */}
          {rect ? (
            <div
              className="absolute rounded-xl transition-all duration-300 pointer-events-none"
              style={{
                top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
                boxShadow: "0 0 0 9999px rgba(30,28,26,0.62)", outline: "3px solid var(--pula-accent)", borderRadius: 14,
              }}
            />
          ) : (
            <div className="absolute inset-0 bg-black/60" />
          )}
          {/* step bubble */}
          <div
            className="absolute w-[300px] max-w-[90vw] bg-white rounded-2xl shadow-2xl border border-[var(--pula-border)] p-4"
            style={bubbleStyle}
            data-testid="page-guide-tour-bubble">
            <div className="flex items-start gap-2.5">
              <img src="/pula-guide.png" alt="" className="w-9 h-9 rounded-full object-cover bg-pula-green/10 shrink-0" />
              <p className="text-sm text-pula-ink font-body leading-relaxed flex-1">{step.text}</p>
              {speechOk && (
                <button data-testid="page-guide-tour-listen" onClick={() => (speaking ? stopSpeak() : speak(step.text))}
                  className="shrink-0 text-pula-green hover:text-pula-greenhover" aria-label="Read step aloud">
                  {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              )}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--pula-border)]">
              <span className="text-xs text-pula-inksoft font-body">{tourIndex + 1} / {guide.steps.length}</span>
              <div className="flex items-center gap-2">
                {tourIndex > 0 && (
                  <button data-testid="page-guide-tour-back" onClick={() => setTourIndex((i) => i - 1)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[var(--pula-border)] text-xs font-heading font-bold text-pula-ink hover:bg-pula-bg2">
                    <ChevronLeft className="w-3.5 h-3.5" /> Back
                  </button>
                )}
                {tourIndex < guide.steps.length - 1 ? (
                  <button data-testid="page-guide-tour-next" onClick={() => setTourIndex((i) => i + 1)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-pula-green text-white text-xs font-heading font-bold hover:bg-pula-greenhover">
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button data-testid="page-guide-tour-done" onClick={closeAll}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-pula-accent text-white text-xs font-heading font-bold hover:bg-pula-accent/90">
                    <HandHelping className="w-3.5 h-3.5" /> Done
                  </button>
                )}
              </div>
            </div>
            <button data-testid="page-guide-tour-close" onClick={closeAll}
              className="absolute -top-2.5 -right-2.5 w-7 h-7 rounded-full bg-white border border-[var(--pula-border)] shadow grid place-items-center text-pula-inksoft hover:text-pula-ink">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
